import { useEffect, useRef, useState } from 'react';
import { API_BASE, tokenStorage } from '@/services/apiClient';
import { playbackHeaders } from '@/services/playbackHeaders';
import { isScreenLimitBody } from '@/services/screenLimit';
import { useAppDispatch } from '@/store';
import { screenLimitReached } from '@/store/slices/screenSlice';

/**
 * State machine for the cold-click pre-resolve flow on `/api/sports-vod-stream/:id/playlist.m3u8`:
 *
 *   idle    → no fetch in flight yet (initial mount, before we have a URL)
 *   pending → server returned HTTP 202 + sseUrl; we're subscribed and waiting
 *             for the resolver-executor to write `status:'fresh'`
 *   ready   → server returned 200; the playlist body is now servable by HLS.js
 *   failed  → server returned a hard error, or SSE delivered failed/unwatchable
 *             /timeout. Caller shows a retry CTA bound to `bumpRetry`.
 *
 * Caller passes `playlistUrl` (the same URL HLS.js would fetch). We preflight
 * it. On 202 we open an EventSource against the sseUrl from the body and wait;
 * on 'fresh' we re-preflight. The hook deliberately does NOT do the HLS fetch
 * itself — HLS.js still does the real fetch when the caller mounts the player
 * after status='ready'. That second fetch hits L2 fresh (just-written) and is
 * cheap.
 */
export type SportsVodResolveState = 'idle' | 'pending' | 'ready' | 'failed';

interface UseSportsVodResolveStatusArgs {
  /** Same URL the caller will hand to useHlsPlayer once we report 'ready'. */
  playlistUrl: string;
  /** Bumped by the caller (e.g. via Retry button) to re-trigger the flow. */
  retryToken: number;
  /** Per-client SSE timeout. Default 6 min (matches backend). */
  timeoutMs?: number;
}

export interface UseSportsVodResolveStatusResult {
  state: SportsVodResolveState;
  /** Last error message surfaced from the SSE stream or preflight fetch. */
  error: string | null;
}

const DEFAULT_TIMEOUT_MS = 6 * 60_000;

export function useSportsVodResolveStatus(
  args: UseSportsVodResolveStatusArgs,
): UseSportsVodResolveStatusResult {
  const { playlistUrl, retryToken, timeoutMs = DEFAULT_TIMEOUT_MS } = args;
  const dispatch = useAppDispatch();
  const [state, setState] = useState<SportsVodResolveState>('idle');
  const [error, setError] = useState<string | null>(null);
  const sseRef = useRef<EventSource | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!playlistUrl) {
      setState('idle');
      return;
    }
    let cancelled = false;
    setState('idle');
    setError(null);

    function cleanupSse(): void {
      sseRef.current?.close();
      sseRef.current = null;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    async function preflight(): Promise<void> {
      try {
        const res = await fetch(playlistUrl, {
          method: 'GET',
          headers: playbackHeaders(true),
        });
        if (cancelled) return;
        if (res.status === 200) {
          // We only needed the status — let GC reclaim the body.
          cleanupSse();
          setState('ready');
          return;
        }
        if (res.status === 202) {
          let body: { sseUrl?: string; titleId?: string } = {};
          try { body = await res.json() as typeof body; } catch { /* ignore */ }
          if (!body.sseUrl) {
            setState('failed');
            setError('pending response missing sseUrl');
            return;
          }
          setState('pending');
          openSse(body.sseUrl);
          return;
        }
        // Non-200/202. Surface a screen-limit block via the redux slice so the
        // global ScreenLimitModal can prompt the user to free a device.
        const bodyText = await res.text().catch(() => '');
        if (isScreenLimitBody(res.status, bodyText)) {
          cleanupSse();
          dispatch(screenLimitReached());
          setState('failed');
          setError('screen limit reached');
          return;
        }
        cleanupSse();
        setState('failed');
        setError(`upstream returned ${res.status}`);
      } catch (err) {
        if (cancelled) return;
        cleanupSse();
        setState('failed');
        setError(err instanceof Error ? err.message : 'network error');
      }
    }

    function openSse(sseUrl: string): void {
      const token = tokenStorage.get();
      const url = token
        ? `${API_BASE}${sseUrl}?token=${encodeURIComponent(token)}`
        : `${API_BASE}${sseUrl}`;
      const es = new EventSource(url);
      sseRef.current = es;
      timeoutRef.current = setTimeout(() => {
        if (cancelled) return;
        cleanupSse();
        setState('failed');
        setError('timed out waiting for resolve');
      }, timeoutMs);

      es.onmessage = (event): void => {
        if (cancelled) return;
        let payload: { status?: string } = {};
        try { payload = JSON.parse(event.data) as typeof payload; } catch { /* ignore */ }
        const status = payload.status;
        if (status === 'fresh') {
          cleanupSse();
          // Re-preflight to fetch the now-cached playlist.
          void preflight();
          return;
        }
        if (status === 'failed' || status === 'unwatchable' || status === 'timeout') {
          cleanupSse();
          setState('failed');
          setError(`resolve ${status}`);
        }
        // status === 'pending' → keep waiting
      };
      es.onerror = (): void => {
        if (cancelled) return;
        cleanupSse();
        setState('failed');
        setError('SSE connection error');
      };
    }

    void preflight();

    return (): void => {
      cancelled = true;
      cleanupSse();
    };
  }, [playlistUrl, retryToken, timeoutMs]);

  return { state, error };
}
